import React, { useState, useEffect } from 'react';
import { Plus, DollarSign, Trash2 } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { getOperatingCosts, createOperatingCost, deleteOperatingCost, OperatingCost } from '../../lib/operatingCosts';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal'; 

const categories = [
  'Rent & Utilities',
  'Insurance',
  'Equipment',
  'Software & Services',
  'Marketing',
  'Payroll',
  'Supplies',
  'Other'
];

export default function CoreOperatingCosts() {
  const { selectedBusiness } = useBusiness();
  const [isAddingCost, setIsAddingCost] = useState(false);
  const [costs, setCosts] = useState<OperatingCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [costToDelete, setCostToDelete] = useState<OperatingCost | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    type: 'fixed',
    frequency: 'monthly',
    category: categories[0]
  });
  const [saveLoading, setSaveLoading] = useState(false);

  const loadCosts = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getOperatingCosts(selectedBusiness.id);
      setCosts(data);
      setError(null);
    } catch (err) {
      console.error('Error loading operating costs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load operating costs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCosts();
  }, [selectedBusiness?.id]);

  const handleDelete = (cost: OperatingCost) => {
    setCostToDelete(cost);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!costToDelete) return;
    
    try {
      setDeleteLoading(true);
      await deleteOperatingCost(costToDelete.id);
      await loadCosts();
      setIsDeleteModalOpen(false);
      setCostToDelete(null);
    } catch (err) {
      console.error('Error deleting operating cost:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete operating cost');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedBusiness?.id) {
      setError('No business selected');
      return;
    }

    try {
      setSaveLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Name is required');
      }

      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid amount');
      }

      await createOperatingCost({
        business_id: selectedBusiness.id,
        name: formData.name.trim(),
        amount,
        type: formData.type as 'fixed' | 'variable',
        frequency: formData.frequency as 'monthly' | 'quarterly' | 'yearly',
        category: formData.category
      });

      // Reset form and reload costs
      setFormData({
        name: '',
        amount: '',
        type: 'fixed',
        frequency: 'monthly',
        category: categories[0]
      });
      setIsAddingCost(false);
      await loadCosts();
    } catch (err) {
      console.error('Error saving operating cost:', err);
      setError(err instanceof Error ? err.message : 'Failed to save operating cost');
    } finally {
      setSaveLoading(false);
    }
  };

  // Group costs by category
  const costsByCategory = React.useMemo(() => costs.reduce((acc, cost) => {
    const category = cost.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(cost);
    return acc;
  }, {} as Record<string, typeof costs>), [costs]);

  return (
    <div 
      className="min-h-screen bg-gray-100 p-2 sm:p-4"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
    >
      <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 pb-24">
        {/* Mobile-optimized summary stats */}
        <div className="bg-gray-50 rounded-2xl shadow-lg border border-gray-200/50">
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200/50">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 tracking-tight">Summary</h2>
          </div>
          <div className="p-4 sm:p-8">
            <div className="text-sm sm:text-base text-gray-600 leading-relaxed">
              Operating costs are the core expenses required to run your business. Track fixed and variable costs to better understand your business's financial health and make informed decisions about pricing and resource allocation.
              
              <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200/50">
                  <div className="text-xs sm:text-sm font-medium text-gray-500">Monthly Fixed Costs</div>
                  <div className="mt-1 sm:mt-2 text-xl sm:text-2xl font-semibold text-gray-900">
                    ${costs
                      .filter(c => c.type === 'fixed' && c.frequency === 'monthly')
                      .reduce((sum, c) => sum + c.amount, 0)
                      .toLocaleString()}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200/50">
                  <div className="text-xs sm:text-sm font-medium text-gray-500">Quarterly Costs</div>
                  <div className="mt-1 sm:mt-2 text-xl sm:text-2xl font-semibold text-gray-900">
                    ${costs
                      .filter(c => c.frequency === 'quarterly')
                      .reduce((sum, c) => sum + c.amount, 0)
                      .toLocaleString()}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200/50 sm:col-span-2 lg:col-span-1">
                  <div className="text-xs sm:text-sm font-medium text-gray-500">Annual Costs</div>
                  <div className="mt-1 sm:mt-2 text-xl sm:text-2xl font-semibold text-gray-900">
                    ${costs
                      .filter(c => c.frequency === 'yearly')
                      .reduce((sum, c) => sum + c.amount, 0)
                      .toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl shadow-lg border border-gray-200/50">
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200/50 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 tracking-tight">Operating Costs</h2>
            <button 
              onClick={() => setIsAddingCost(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 text-sm font-semibold text-white bg-purple-700 rounded-xl hover:bg-purple-800 transition-all duration-200 active:scale-95 shadow-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Cost
            </button>
          </div>
          <div className="p-4 sm:p-8">
            {/* Mobile-optimized Add Cost Form */}
            {isAddingCost && (
              <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-white rounded-2xl border border-gray-200/50 shadow-sm">
                <div className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                        Name
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
                        placeholder="Enter cost name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                        Amount
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm sm:text-base">$</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.amount}
                          onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                          className="w-full pl-7 sm:pl-8 pr-3 sm:pr-4 h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                        Type
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'fixed' | 'variable' }))}
                        className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
                      >
                        <option value="fixed">Fixed</option>
                        <option value="variable">Variable</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                        Frequency
                      </label>
                      <select
                        value={formData.frequency}
                        onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value as 'monthly' | 'quarterly' | 'yearly' }))}
                        className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2 lg:col-span-1">
                      <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
                      >
                        {categories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                    <button
                      onClick={() => setIsAddingCost(false)}
                      className="w-full sm:w-auto px-6 py-3 text-sm sm:text-base font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 active:scale-95 order-2 sm:order-1"
                      disabled={saveLoading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saveLoading || !formData.name.trim() || !formData.amount}
                      className="w-full sm:w-auto px-6 py-3 text-sm sm:text-base font-semibold text-white bg-purple-700 rounded-xl hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95 order-1 sm:order-2"
                    >
                      {saveLoading ? 'Saving...' : 'Save Cost'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Mobile-optimized Costs List */}
            <div className="space-y-6 sm:space-y-8">
              {categories.map(category => (
                // Filter costs by category
                costs.filter(cost => cost.category === category).length > 0 && (
                <div key={category}>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">{category}</h3>
                  <div className="bg-white rounded-2xl border border-gray-200/50 shadow-sm divide-y divide-gray-200/50">
                    {costs
                      .filter(cost => cost.category === category)
                      .map(cost => (
                        <div key={cost.id} className="p-4 sm:p-6">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm sm:text-base font-semibold text-gray-900 truncate">{cost.name}</h4>
                              <div className="mt-2 flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-500">
                                <span className={`px-2 sm:px-3 py-1 rounded-full font-medium text-xs sm:text-sm ${
                                  cost.type === 'fixed' 
                                    ? 'bg-blue-100 text-blue-700' 
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {cost.type.charAt(0).toUpperCase() + cost.type.slice(1)}
                                </span>
                                <span className="font-medium">
                                  {cost.frequency.charAt(0).toUpperCase() + cost.frequency.slice(1)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between sm:justify-end sm:space-x-6">
                              <div className="text-lg sm:text-xl font-semibold text-gray-900">
                                ${cost.amount.toLocaleString()}
                              </div>
                              <button 
                                onClick={() => handleDelete(cost)}
                                className="p-2 sm:p-2 text-red-400 hover:text-red-600 rounded-xl hover:bg-red-50 transition-all duration-200 active:scale-95 touch-manipulation"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
                )
              ))}
              {costs.length === 0 && (
                <div className="text-center py-8 sm:py-12">
                  <DollarSign className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                  <p className="text-base sm:text-lg font-semibold text-gray-900">No operating costs</p>
                  <p className="text-sm sm:text-base text-gray-500 mt-1 sm:mt-2">
                    Start adding operating costs to track your business expenses
                  </p>
                  <button
                    onClick={() => setIsAddingCost(true)}
                    className="mt-4 sm:mt-6 w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 text-sm sm:text-base font-semibold text-white bg-purple-700 hover:bg-purple-800 rounded-xl transition-all duration-200 active:scale-95"
                  >
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    Add Operating Cost
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setCostToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Operating Cost"
          message={`Are you sure you want to delete "${costToDelete?.name}"? This action cannot be undone.`}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
}